import moment from "moment";
import {
  ComplianceStatusType,
  ResultStatusType,
  StatusType,
  ThemeMode,
  getStatusDefinition,
  getStatusDotColor,
  getStatusMainColor,
  getStatusStyle,
  compareCompliancePriority,
  compareResultPriority,
  getCompliancePriority,
  getResultPriority,
  renderStatusIcon,
  getStatusIconComponent,
} from "../constants/status";
import { WidgetTestResult } from "../models/machine-test-result.model";
import { TestRunStatus } from "../models/test-run.model";

export function resolveThemeMode(paletteType?: string): ThemeMode {
  return paletteType === "dark" ? "dark" : "light";
}

export function getStatusColor(status: StatusType, mode: ThemeMode): string {
  return getStatusMainColor(status, mode);
}

export function getStatusLabel(status: StatusType): string {
  return getStatusDefinition(status).label;
}

export function getStatusTooltip(status: StatusType): string | undefined {
  return getStatusDefinition(status).tooltip;
}

export function getStatusPriority(status: StatusType): number {
  if (status === "pass" || status === "fail" || status === "failed_resolved") {
    return getResultPriority(status);
  }
  return getCompliancePriority(status as ComplianceStatusType);
}

export { getStatusStyle, getStatusDotColor, getStatusIconComponent, renderStatusIcon };

export { getStatusIconColor } from "../constants/status/statusColors";

export function aggregateComplianceStatuses(statuses: ComplianceStatusType[]): ComplianceStatusType {
  if (!statuses.length) {
    return "complete";
  }
  return statuses.reduce((highest, current) =>
    compareCompliancePriority(current, highest) > 0 ? current : highest
  );
}

export function aggregateResultStatuses(statuses: ResultStatusType[]): ResultStatusType {
  if (!statuses.length) {
    return "pass";
  }
  return statuses.reduce((highest, current) =>
    compareResultPriority(current, highest) > 0 ? current : highest
  );
}

export interface DeviceTestComplianceInput {
  dueAt?: Date | string | null;
  due?: boolean;
  overdue?: boolean;
  partial?: boolean;
  daysSinceLastRun?: number | null;
  lastTestRun?: Date | string | null;
  frequency?: number;
  status?: string;
  /**
   * Exact-match "Next_Due_Date == Current Local Date" (Story #12572) - distinct from `due` above,
   * which keeps its original "due or overdue" meaning (used for the Due/Overdue filters).
   */
  dueToday?: boolean;
  /** Compliance status flags (Story #12572), computed server-side in the facility's local timezone. */
  missed?: boolean;
  incomplete?: boolean;
  pending?: boolean;
  skipped?: boolean;
}

/**
 * QA Details status input (Story #12572).
 * Uses the due/missed/incomplete/pending/skipped flags from getQATests (existing API fields,
 * computed server-side against each facility's local calendar day).
 */
export interface QADetailsStatusInput extends DeviceTestComplianceInput {}

/**
 * Optional, caller-specific status rules for getQADetailsStatus (and the Label / Tooltip /
 * aggregate helpers that wrap it). Every option defaults to the legacy behavior, so QA Details
 * (which passes none) is unaffected. The Calendar supplies CALENDAR_STATUS_OPTIONS
 * (app/components/Calendar/calendarStatusConfig.ts).
 */
export interface QADetailsStatusOptions {
  /**
   * Which status set the caller displays.
   *  - "compliance" (default): the full Story #12572 set - Missed > Incomplete > Pending > Skipped >
   *    Due > Not Due (used by the Calendar drawer and any caller passing no options).
   *  - "current": the CURRENT state of the present cycle only - Overdue > Pending > Due > Not Due.
   *    Missed / Incomplete / Skipped never appear. Used by the QA Details page
   *    (QA_DETAILS_STATUS_OPTIONS). Read from the server's facility-timezone flags, see
   *    getCurrentPeriodStatus.
   */
  statusModel?: "compliance" | "current";
  /**
   * How a test reads on TODAY when its due day has already passed (its cycle closed and it has not
   * been run) and no Missed/Incomplete/Pending/Skipped flag applies. Only used when an asOfDate is
   * supplied and that date is today. "due" (default) = legacy; "overdue" = the Overdue status.
   * A test whose due day IS today is always "due".
   */
  pastDueStatus?: "due" | "overdue";
  /**
   * Daily tests only: a due day that falls on Sat/Sun rolls forward to Monday before the
   * past-due check, so a daily test last run on Friday is "Due" on Monday, not "Overdue"
   * (daily QA is not run on weekends - see the Calendar's includeWeekends=false default).
   */
  skipWeekendsForDaily?: boolean;
}

/**
 * QA Details "current period" status (statusModel "current"). Derived only from flags the API
 * already computes per row in the facility's local timezone (computeQAComplianceFlags in
 * zapit-api qa-compliance.ts), so no browser-timezone date math is involved:
 *  - Pending : not yet due AND partial sub-test progress in the current cycle (`pending`)
 *  - Overdue : the due date has passed and the cycle is not complete (`overdue`) - covers what
 *              the compliance model splits into Missed (nothing done) and Incomplete (partial)
 *  - Due     : due today, or never run (`dueToday`)
 *  - Not Due : everything else. A last run that was Skipped no longer has its own status; the
 *              row shows its date status instead.
 * The four are mutually exclusive (pending needs "not yet due", overdue "past due", dueToday
 * "today"), so the order below is only a safety net. Matches the QA Details filters: Overdue ->
 * `overdue`, Pending -> `pending`, Due -> `dueToday`, Not Due -> `notDueStatus`.
 */
export function getCurrentPeriodStatus(test: QADetailsStatusInput): ComplianceStatusType {
  if (test.overdue) {
    return "overdue";
  }
  if (test.pending) {
    return "pending";
  }
  if (test.dueToday) {
    return "due";
  }
  return "not_due";
}

/**
 * The test's due CALENDAR day, local midnight. dueAt is a UTC date (DATE(...) in SQL ->
 * "YYYY-MM-DDT00:00:00Z"), so it is read with moment.utc - a local moment(dueAt) would put a US
 * browser on the previous day (see zapit-api/docs/TIMEZONE_AND_DATE_HANDLING.md section 2.3).
 */
function getDueCalendarDay(test: QADetailsStatusInput, options?: QADetailsStatusOptions): moment.Moment | null {
  if (!test.dueAt) {
    return null;
  }
  const utc = moment.utc(test.dueAt);
  if (!utc.isValid()) {
    return null;
  }
  const day = moment(utc.format("YYYY-MM-DD"), "YYYY-MM-DD");
  if (options?.skipWeekendsForDaily && test.frequency === 1) {
    while (day.day() === 0 || day.day() === 6) {
      day.add(1, "day");
    }
  }
  return day;
}

/** Checked in this order - the highest-priority true flag wins (see COMPLIANCE_STATUS_PRIORITY). */
const QA_COMPLIANCE_FLAG_KEYS: ComplianceStatusType[] = ["missed", "incomplete", "pending", "skipped"];

/**
 * QA Details status column (Story #12572).
 *
 * Backend computes each condition independently (missed/incomplete/pending/skipped, plus due) in
 * the facility's local timezone; this picks the highest-priority one that applies -
 * Missed > Incomplete > Pending > Skipped > Due > Not Due (see COMPLIANCE_STATUS_PRIORITY).
 *
 * `dueToday` is an exact match only (Next_Due_Date == Current Local Date) - a test that's overdue
 * by several days falls through to Missed/Incomplete above instead, and everything else (due date
 * still ahead, with none of the other flags active) is Not Due. Note this is `dueToday`, not the
 * plain `due` flag - `due` keeps its original "due or overdue" meaning for the Due/Overdue filters.
 *
 * `asOfDate` (optional, defaults to the current day) reframes "is this test due" from the
 * perspective of a specific calendar day rather than today - the Calendar passes the tile / the
 * selected day so a test whose `dueAt` falls on that day reads as "Due" there (and one whose
 * window has not opened by that day reads as "Not Due"), instead of everything being judged
 * against the real today. QA Details omits it and keeps the today-relative behavior.
 */
export function getQADetailsStatus(test: QADetailsStatusInput, asOfDate?: moment.Moment | Date | string, options?: QADetailsStatusOptions): StatusType {
  if (options?.statusModel === "current") {
    return getCurrentPeriodStatus(test);
  }

  const activeStatuses = QA_COMPLIANCE_FLAG_KEYS.filter((key) => !!(test as Record<ComplianceStatusType, boolean | undefined>)[key]);

  const asOf = (asOfDate ? moment(asOfDate) : moment()).startOf("day");

  // A test whose next due date is on/after `asOf` has not missed its compliance window as of that
  // day - it is "Due" (window opens on `asOf`) or "Not Due" (window opens later). Missed/Incomplete
  // describe an expired window only, so drop those flags when dueAt has not passed `asOf` yet
  // (defends against a stale/blanket flag from the aggregation upstream - see the Calendar
  // "future date shows Missed" fix). Pending and Skipped are still meaningful while the window is
  // open, so they are kept.
  const dueAtRaw = test.dueAt ? moment(test.dueAt) : null;
  const dueDay = dueAtRaw && dueAtRaw.isValid() ? dueAtRaw.clone().startOf("day") : null;
  const windowOpen = !!dueDay && dueDay.isSameOrAfter(asOf, "day");
  const effectiveStatuses = windowOpen ? activeStatuses.filter((s) => s !== "missed" && s !== "incomplete") : activeStatuses;

  if (effectiveStatuses.length) {
    return aggregateComplianceStatuses(effectiveStatuses);
  }

  // Default (no asOfDate): keep the exact `dueToday` semantics QA Details relies on. With an
  // asOfDate: the test is "Due" on the day its window opens - dueAt on `asOf`, OR dueAt already
  // passed and `asOf` is today (an overdue test the Calendar buckets onto today's tile).
  if (!asOfDate) {
    return test.dueToday ? "due" : "not_due";
  }
  if (!dueDay) {
    return "not_due";
  }
  const asOfIsToday = asOf.isSame(moment().startOf("day"), "day");
  if (dueDay.isSame(asOf, "day") || (asOfIsToday && dueDay.isSameOrBefore(asOf, "day"))) {
    // Caller opted in (the Calendar): a test whose due day is strictly before today has crossed
    // its cycle without a run - "Overdue" rather than "Due". Judged on the UTC due day so a US
    // browser does not see a test due today as due "yesterday".
    if (asOfIsToday && options?.pastDueStatus === "overdue") {
      const dueCalendarDay = getDueCalendarDay(test, options);
      if (dueCalendarDay && dueCalendarDay.isBefore(asOf, "day")) {
        return "overdue";
      }
    }
    return "due";
  }
  return "not_due";
}

export function getQADetailsStatusLabel(test: QADetailsStatusInput, asOfDate?: moment.Moment | Date | string, options?: QADetailsStatusOptions): string {
  const readyStatus = (test.status ?? "ready").toLowerCase();

  // Non-ready setup states keep their raw API status text (e.g. "Procedure is not set")
  if (readyStatus !== "ready") {
    return test.status || getStatusLabel(getQADetailsStatus(test, asOfDate, options));
  }

  return getStatusLabel(getQADetailsStatus(test, asOfDate, options));
}

/**
 * Aggregates compliance status across a group of device tests (e.g. all tests due on a calendar
 * day, or all tests for one machine+frequency group) - a thin composition of the existing
 * getQADetailsStatus (per-test status) and aggregateComplianceStatuses (priority reducer). Does
 * not duplicate any priority logic.
 *
 * Cast is safe: getQADetailsStatus is typed StatusType for callers that may pass "excluded"/result
 * statuses through it elsewhere, but for QADetailsStatusInput it only ever returns
 * ComplianceStatusType members (see its implementation).
 */
export function aggregateDeviceTestGroupComplianceStatus(
  tests: DeviceTestComplianceInput[],
  asOfDate?: moment.Moment | Date | string,
  options?: QADetailsStatusOptions
): ComplianceStatusType {
  return aggregateComplianceStatuses(tests.map((test) => getQADetailsStatus(test, asOfDate, options) as ComplianceStatusType));
}

export function getQADetailsStatusTooltip(test: QADetailsStatusInput, asOfDate?: moment.Moment | Date | string, options?: QADetailsStatusOptions): string | undefined {
  const status = getQADetailsStatus(test, asOfDate, options);

  if (status === "overdue" && options?.statusModel !== "current") {
    // Calendar (pastDueStatus): its dueAt is a UTC date - read the UTC due day (getDueCalendarDay).
    // QA Details' dueAt is a facility-local midnight instant, so it falls through to the local
    // formatting below, the same as its Due / Missed tooltips have always used.
    const dueCalendarDay = getDueCalendarDay(test);
    return dueCalendarDay ? `Due date was ${dueCalendarDay.format("M/D/YYYY")}` : getStatusTooltip(status);
  }
  const dueAt = test.dueAt ? moment(test.dueAt) : null;

  if (!dueAt || !dueAt.isValid()) {
    return getStatusTooltip(status);
  }

  const formattedDate = dueAt.format("M/D/YYYY");

  if (status === "due") {
    return `Due on ${formattedDate}`;
  }

  if (status === "missed" || status === "incomplete" || status === "overdue") {
    return `Due date was ${formattedDate}`;
  }

  return getStatusTooltip(status);
}

export interface TestResultGroupInput {
  status?: string;
  resolved?: boolean;
  exclude?: boolean;
  testVersionId?: number;
  subtestEnabledCount?: number;
}

/**
 * Aggregates result statuses for a group of test results on a calendar day.
 * Priority: Skipped > Fail > Incomplete > Failed & Resolved > Pass
 */
export function aggregateTestResultGroupStatus(items: TestResultGroupInput[]): ResultStatusType | "skipped" | "incomplete" {
  if (!items.length) {
    return "pass";
  }

  const hasSkipped = items.some((t) => t.status === TestRunStatus.notRun);
  if (hasSkipped) {
    return "skipped";
  }

  const hasFail = items.some((t) => t.status === TestRunStatus.fail && !t.resolved);
  if (hasFail) {
    return "fail";
  }

  const firstItem = items[0];
  const enabledSubtestCount = firstItem.subtestEnabledCount ?? 0;
  const uniqueTestVersionIds = new Set(items.map((t) => t.testVersionId)).size;
  if (enabledSubtestCount > 0 && uniqueTestVersionIds < enabledSubtestCount) {
    return "incomplete";
  }

  const hasResolved = items.some((t) => t.status === TestRunStatus.fail && t.resolved);
  if (hasResolved) {
    return "failed_resolved";
  }

  return "pass";
}

const RESULT_DAY_PRIORITY: StatusType[] = ["skipped", "fail", "incomplete", "failed_resolved", "pass"];

function getResultDayPriorityRank(status: StatusType): number {
  const index = RESULT_DAY_PRIORITY.indexOf(status);
  return index >= 0 ? RESULT_DAY_PRIORITY.length - index : 0;
}

/**
 * Aggregates all test results on a calendar day across machine/test groups.
 * Priority: Skipped > Fail > Incomplete > Failed & Resolved > Pass
 */
export function aggregateTestResultDayStatus(items: WidgetTestResult[], dateFormat: string): StatusType {
  if (!items.length) {
    return "pass";
  }

  const groupedByTest: Record<string, WidgetTestResult[]> = {};

  items.forEach((item) => {
    const key = `${item.machineId}_${item.testId}_${moment(item.testDate).format(dateFormat)}`;
    if (!groupedByTest[key]) {
      groupedByTest[key] = [];
    }
    groupedByTest[key].push(item);
  });

  let highestStatus: StatusType = "pass";

  for (const group of Object.values(groupedByTest)) {
    const groupStatus = mapResultStatusToDisplayStatus(aggregateTestResultGroupStatus(group));
    if (getResultDayPriorityRank(groupStatus) > getResultDayPriorityRank(highestStatus)) {
      highestStatus = groupStatus;
    }
  }

  return highestStatus;
}

export function getResultDotStatusFromGroup(items: WidgetTestResult[], dateFormat?: string): StatusType {
  if (dateFormat) {
    return aggregateTestResultDayStatus(items, dateFormat);
  }
  return mapResultStatusToDisplayStatus(aggregateTestResultGroupStatus(items));
}

export function mapResultStatusToDisplayStatus(status: ResultStatusType | "skipped" | "incomplete"): StatusType {
  if (status === "skipped") {
    return "skipped";
  }
  if (status === "incomplete") {
    return "incomplete";
  }
  return status;
}

/**
 * Maps an Audit Report Summary `status` string (as produced by
 * AuditComplianceService.applyRollingCompliance and served by the Calendar compliance-grid
 * endpoint) to a ComplianceStatusType. Anything not recognised - "Complete", "No Data", "" -
 * collapses to "complete" so it never outranks a real Missed/Incomplete/Pending/Skipped.
 */
export function mapAuditStatusToCompliance(status: string | undefined | null): ComplianceStatusType {
  switch ((status || "").toLowerCase()) {
    case "missed":
      return "missed";
    case "incomplete":
      return "incomplete";
    case "pending":
      return "pending";
    case "skipped":
      return "skipped";
    default:
      return "complete";
  }
}

/**
 * Maps an Audit Report Summary `result` string ("Fail" | "Resolved" | "Pass" | "N/A" | "") to a
 * StatusType, or null when there is no quantitative result to show.
 */
export function mapAuditResultToStatus(result: string | undefined | null): StatusType | null {
  switch ((result || "").toLowerCase()) {
    case "fail":
      return "fail";
    case "resolved":
      return "failed_resolved";
    case "pass":
      return "pass";
    default:
      return null;
  }
}

/**
 * Frequency label ("Daily" | "Weekly" | "Bi-Weekly" | "Monthly" | "Quarterly" | "Semi-Annual" |
 * "Yearly", any casing / hyphenation) -> its length in days. Matches the backend's
 * AuditComplianceService#_getFrequencyDays substring logic so the Calendar never silently
 * defaults an unmapped label to Daily (1). Returns 0 when the label is unrecognised - callers
 * should treat 0 as "unknown", never as daily.
 */
export function frequencyDaysFromLabel(label: string | undefined | null): number {
  const n = (label || "").toLowerCase().trim();
  if (!n) return 0;
  if (n.includes("biweekly") || n.includes("bi-weekly") || n.includes("bi weekly")) return 14;
  if (n.includes("weekly")) return 7;
  if (n.includes("daily")) return 1;
  if (n.includes("monthly")) return 30;
  if (n.includes("quarter")) return 91;
  if (n.includes("semi")) return 183;
  // "Bi-Annual" is every two years (frequency.val 730) - check it before the plain "annual" match.
  if (n.includes("biannual") || n.includes("bi-annual") || n.includes("bi annual")) return 730;
  if (n.includes("year") || n.includes("annual")) return 365;
  const num = Number((n.match(/\d+/) || [])[0]);
  return Number.isFinite(num) && num > 0 ? num : 0;
}

/**
 * Aggregates the compliance status for one calendar tile from the date-wise / frequency-wise
 * grid rows that fall on that date (Story #12571 AC 3). Reuses the existing compliance priority
 * reducer - Missed > Incomplete > Pending > Skipped > Complete.
 */
export function aggregateCalendarComplianceGrid(rows: { status?: string | null }[]): ComplianceStatusType {
  return aggregateComplianceStatuses(rows.map((r) => mapAuditStatusToCompliance(r.status)));
}

/**
 * Aggregates the result status for one calendar tile from the grid rows on that date, kept
 * independent of the compliance channel (AC 4 - a Fail marker is never suppressed). Priority:
 * Fail > Failed & Resolved > Pass; null when no row carries a result.
 */
export function aggregateCalendarResultGrid(rows: { result?: string | null }[]): StatusType | null {
  const mapped = rows.map((r) => mapAuditResultToStatus(r.result)).filter((s): s is ResultStatusType => s === "fail" || s === "failed_resolved" || s === "pass");
  if (!mapped.length) {
    return null;
  }
  return aggregateResultStatuses(mapped);
}
